import {
  type CreateSession,
  type ListSessionsQuery,
  type SessionDetail,
  SessionDetailSchema,
  type SessionPage,
  SessionPageSchema,
} from '@sbc/domain-models/agents';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiGet, apiSend } from './client';

const SESSIONS_KEY = ['admin', 'sessions'];

function listKey(query: ListSessionsQuery) {
  return [...SESSIONS_KEY, 'list', query] as const;
}

function detailKey(id: string) {
  return [...SESSIONS_KEY, 'detail', id] as const;
}

export function useSessions(query: ListSessionsQuery) {
  const params = new URLSearchParams({
    page: String(query.page),
    pageSize: String(query.pageSize),
  });
  if (query.search) {
    params.set('search', query.search);
  }
  if (query.agentId) {
    params.set('agentId', query.agentId);
  }
  if (query.status) {
    params.set('status', query.status);
  }
  return useQuery<SessionPage>({
    queryKey: listKey(query),
    queryFn: () => apiGet(`/api/admin/sessions?${params.toString()}`, SessionPageSchema),
  });
}

export function useSession(id: string) {
  return useQuery<SessionDetail>({
    queryKey: detailKey(id),
    queryFn: () => apiGet(`/api/admin/sessions/${id}`, SessionDetailSchema),
  });
}

export function useCreateSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSession) =>
      apiSend('POST', '/api/admin/sessions', input, SessionDetailSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSIONS_KEY }),
  });
}

export function usePostMessage(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      apiSend('POST', `/api/admin/sessions/${id}/messages`, { content }, SessionDetailSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSIONS_KEY }),
  });
}

export function useRunSession(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiSend('POST', `/api/admin/sessions/${id}/run`, {}, SessionDetailSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSIONS_KEY }),
  });
}
